import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Save, Send, CheckCircle2, XCircle, Plus, Trash2, Printer, FileSpreadsheet, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { exportToExcel } from '../utils/excelExport';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmtR = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0);

const emptyRow = (template) => ({ ...template });
const TEMPLATES = {
  product: { name: '', working_months: 12, duration_unit: 'Month', prod_per_duration: 0, utilization_pct: 100, sale_price_per_unit: 0, cost_per_unit: 0 },
  other_income: { description: '', amount: 0 },
  land_building: { ownership: 'Self', amount: 0 },
  plant_machinery: { particulars: '', nos: 1, unit_price: 0, scrap_value: 0, supplier: '', life_years: 5 },
  furniture: { particulars: '', nos: 1, unit_price: 0, scrap_value: 0, supplier: '', life_years: 5 },
  raw_material: { item: '', frequency_per_year: 12, quantity: 0, unit_rate: 0 },
  utilities: { type: '', yearly_amount: 0 },
  manpower: { role: '', monthly_wage: 0, months: 12, count: 1 },
  admin_expenses: { type: '', yearly_amount: 0 },
  selling_expenses: { type: '', yearly_amount: 0 },
  working_capital: { type: '', holding_days: 0, holding_qty: 0, unit_rate: 0, description: '' },
  pre_operative_items: { type: '', type_other: '', description: '', amount: 0 },
};

// Section component: an editable array-of-rows table
const RowsTable = ({ title, subtitle, rows, columns, onChange, addLabel, readOnly, computedFooter }) => {
  const updateCell = (idx, key, value) => {
    const next = rows.map((r, i) => i === idx ? { ...r, [key]: value } : r);
    onChange(next);
  };
  const addRow = () => onChange([...(rows || []), emptyRow(columns.template)]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
      <div className="flex justify-between items-center gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {!readOnly && <Button size="sm" variant="outline" onClick={addRow} className="h-8 text-sm flex-shrink-0"><Plus className="w-4 h-4 mr-1" />{addLabel || 'Row'}</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className={`text-sm ${columns.minWidth || 'w-full'}`} style={columns.minWidth ? { minWidth: columns.minWidth } : undefined}>
          <thead className="bg-slate-100"><tr>{columns.cols.map(c => <th key={c.key} className={`px-2.5 py-2 font-semibold text-slate-700 whitespace-nowrap ${c.align || 'text-left'}`} style={c.minWidth ? { minWidth: c.minWidth } : undefined}>{c.label}</th>)}{!readOnly && <th className="w-8" />}</tr></thead>
          <tbody>
            {(rows || []).length === 0 ? (
              <tr><td colSpan={columns.cols.length + 1} className="px-2 py-4 text-center text-slate-400 italic">No entries</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r._rowId || `${i}-${r.id || r.type || r.item || ''}`} className={i % 2 ? 'bg-slate-50' : ''}>
                {columns.cols.map(c => (
                  <td key={c.key} className={`px-1.5 py-1.5 ${c.align || ''}`} style={c.minWidth ? { minWidth: c.minWidth } : undefined}>
                    {c.compute
                      ? <span className="text-slate-900 font-semibold px-1">{c.format ? c.format(c.compute(r)) : c.compute(r)}</span>
                      : c.type === 'select'
                      ? (
                          <select
                            value={r[c.key] ?? c.default ?? ''}
                            onChange={e => updateCell(i, c.key, e.target.value)}
                            disabled={readOnly}
                            className="h-9 text-sm border border-slate-300 rounded-md px-2 bg-white text-slate-900 font-medium"
                          >
                            {(c.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        )
                      : c.type === 'select-other'
                      ? (
                          <div className="flex gap-1">
                            <select
                              value={r[c.key] ?? c.default ?? ''}
                              onChange={e => updateCell(i, c.key, e.target.value)}
                              disabled={readOnly}
                              className="h-9 text-sm border border-slate-300 rounded-md px-2 bg-white text-slate-900 font-medium flex-1 min-w-0"
                            >
                              <option value="">— Select —</option>
                              {(c.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                            {((r[c.key] || '').toLowerCase() === 'other' || (r[c.key] || '').toLowerCase() === 'others') && (
                              <Input
                                placeholder="Specify…"
                                value={r[`${c.key}_other`] ?? ''}
                                onChange={e => updateCell(i, `${c.key}_other`, e.target.value)}
                                className="h-9 text-sm flex-1 min-w-0 text-slate-900 font-medium"
                                disabled={readOnly}
                              />
                            )}
                          </div>
                        )
                      : <Input value={r[c.key] ?? ''} type={c.type || 'text'} onChange={e => updateCell(i, c.key, c.type === 'number' ? Number(e.target.value || 0) : e.target.value)} className="h-9 text-sm text-slate-900 font-medium" disabled={readOnly} />
                    }
                  </td>
                ))}
                {!readOnly && <td className="px-1 py-1"><button onClick={() => removeRow(i)} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-3 h-3" /></button></td>}
              </tr>
            ))}
          </tbody>
          {computedFooter && (
            <tfoot className="bg-emerald-50 font-semibold text-emerald-900">
              <tr>{computedFooter}</tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const BusinessPlanForm = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [entrepreneur, setEntrepreneur] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canApprove = ['admin', 'checker', 'hr', 'account', 'branch'].includes((user?.role || '').toLowerCase());
  const isReadOnly = !!plan && (plan.status === 'pending_approval' || plan.status === 'approved');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/business-plans/${id}`);
      setPlan(r.data);
      const e = await api.get(`/entrepreneurs/${r.data.entrepreneur_id}`);
      setEntrepreneur(e.data);
      try { const br = await api.get(`/branches/${r.data.branch_id}`); setBranch(br.data); }
      catch { /* fallback */ }
    } catch { toast.error('Failed to load plan'); navigate('/business-plans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateField = (key, value) => setPlan(p => ({ ...p, [key]: value }));

  // ---------- Derived computations (live) ----------
  const calc = useMemo(() => {
    if (!plan) return {};
    const products = plan.products || [];
    const productRows = products.map(p => {
      // Convert working duration to total yearly cycles based on the selected unit
      // Day → working_months represents days in year; Week → weeks; Month → months
      const unit = p.duration_unit || 'Month';
      const cyclesPerYear = unit === 'Day' ? num(p.working_months)
        : unit === 'Week' ? num(p.working_months)
        : num(p.working_months); // Month — same numeric input
      const perDur = num(p.prod_per_duration);
      const totalProd = perDur > 0
        ? cyclesPerYear * perDur
        : num(p.qty_per_year || 0);
      const utilPct = num(p.utilization_pct);
      const perDurUtil = perDur * utilPct / 100;
      const totalUtil = totalProd * utilPct / 100;
      const revenue = totalUtil * num(p.sale_price_per_unit);
      const cost = totalUtil * num(p.cost_per_unit);
      const profit = revenue - cost;
      return { ...p, _perDur: perDur, _totalProd: totalProd, _perDurUtil: perDurUtil, _totalUtil: totalUtil, _revenue: revenue, _cost: cost, _profit: profit };
    });
    const totalRevenue = productRows.reduce((s, p) => s + p._revenue, 0);
    const totalProductCost = productRows.reduce((s, p) => s + p._cost, 0);
    const otherIncome = (plan.other_income || []).reduce((s, r) => s + num(r.amount), 0);
    const totalIncome = totalRevenue + otherIncome;

    const totalLand = (plan.land_building || []).reduce((s, r) => s + num(r.amount), 0);
    const totalMachinery = (plan.plant_machinery || []).reduce((s, r) => s + num(r.nos) * num(r.unit_price), 0);
    const totalFurniture = (plan.furniture || []).reduce((s, r) => s + num(r.nos) * num(r.unit_price), 0);
    const totalRaw = (plan.raw_material || []).reduce((s, r) => s + num(r.frequency_per_year) * num(r.quantity) * num(r.unit_rate), 0);
    const totalUtilities = (plan.utilities || []).reduce((s, r) => s + num(r.yearly_amount), 0);
    const totalManpower = (plan.manpower || []).reduce((s, r) => s + num(r.monthly_wage) * num(r.months) * num(r.count), 0);
    const totalAdmin = (plan.admin_expenses || []).reduce((s, r) => s + num(r.yearly_amount), 0);
    const totalSelling = (plan.selling_expenses || []).reduce((s, r) => s + num(r.yearly_amount), 0);
    const preLoanInterest = num(plan.pre_loan_interest);

    // Depreciation = (cost - scrap_value) / life for plant + furniture (Excel I43/I53 use straight-line over life with scrap residual)
    const depPlant = (plan.plant_machinery || []).reduce((s, r) => {
      const total = num(r.nos) * num(r.unit_price);
      const scrap = num(r.scrap_value);
      const life = num(r.life_years) || 1;
      return s + Math.max(0, (total - scrap) / life);
    }, 0);
    const depFurn = (plan.furniture || []).reduce((s, r) => {
      const total = num(r.nos) * num(r.unit_price);
      const scrap = num(r.scrap_value);
      const life = num(r.life_years) || 1;
      return s + Math.max(0, (total - scrap) / life);
    }, 0);
    const depreciation = depPlant + depFurn;

    // Book value of fixed assets (Excel J = E - I)
    const bkValPlant = totalMachinery - depPlant;     // Excel L48 = SUM(J43:J47)
    const bkValFurn = totalFurniture - depFurn;       // Excel K57 = J57 = SUM(J53:J56)

    // Total Cost of Production = Raw + Utilities + Manpower + Admin + Selling + PreLoanInt
    // (Excel F49 = SUM(F43:H48) covering rows i..vii — matches exactly)
    const totalCostOfProduction = totalRaw + totalUtilities + totalManpower + totalAdmin + totalSelling + preLoanInterest;
    // Net Profit (Excel F51 = F40 − F49); depreciation shown separately below
    const netProfit = totalRevenue - totalCostOfProduction;

    // Pre-operative expense — sum of items if any; fallback to legacy single-number field
    const preOperativeItems = plan.pre_operative_items || [];
    const preOperativeFromItems = preOperativeItems.reduce((s, r) => s + num(r.amount), 0);
    const preOperativeExpense = preOperativeItems.length > 0 ? preOperativeFromItems : num(plan.pre_operative_expense);

    // Working capital — Section 3.9 (matches Excel rows J102..J107):
    //   Each row's inventory_cost = qty × unit_rate
    //   Plus auto row "Others Exp of 1 month" = (Utilities + Manpower + Admin + Selling) / 12
    const workingCapitalRows = (plan.working_capital || []).map(r => ({
      ...r,
      _inv_cost: num(r.holding_qty) * num(r.unit_rate),
    }));
    const wcRowsSum = workingCapitalRows.reduce((s, r) => s + r._inv_cost, 0);
    const wcMonthlyOverhead = (totalUtilities + totalManpower + totalAdmin + totalSelling) / 12;
    const workingCapital = wcRowsSum + wcMonthlyOverhead;

    const totalProjectCost = preOperativeExpense + totalLand + totalMachinery + totalFurniture + workingCapital;

    // ---- Break-even Point ----
    // Standard accounting BEP using ANNUAL flows only:
    //   Fixed Cost  = Pre-op + Land/Building + Plant book-val + Furniture book-val + Utilities + Admin + Pre-loan Int
    //                 (one-time + non-output-scaling overheads)
    //   Variable Cost = Raw Material + Manpower + Selling/Distribution
    //                   (annual costs that scale with output — Working Capital is a STOCK BALANCE,
    //                    not a recurring annual cost, so it's excluded here)
    //   BEP (Sales-value) = (Fixed × Sales) / (Sales − Variable)   ≡   Fixed / Contribution-Margin-Ratio
    //   BEP Period (months) = (BEP / Sales) × 12
    // Note: The user's offline Excel template puts Working Capital into Variable Cost (cell F56 = K66+K81+H99+J107).
    // That works for small-turnover plans but produces a negative contribution margin for high-turnover plans
    // (Variable Cost > Sales). We follow the standard accounting interpretation so BEP is always meaningful.
    const bepSalesRevenue = totalRevenue;
    const fixedCost = preOperativeExpense + totalLand + bkValPlant + bkValFurn + totalUtilities + totalAdmin + preLoanInterest;
    const variableCost = totalRaw + totalManpower + totalSelling;
    const bepDenom = bepSalesRevenue - variableCost;
    const bep = bepDenom > 0 ? (fixedCost * bepSalesRevenue) / bepDenom : 0;
    const bepMonths = bepSalesRevenue > 0 ? (bep / bepSalesRevenue) * 12 : 0;
    // Was sales sufficient to cover variable costs at all? If not, BEP is unreachable — flag it.
    const bepUnreachable = bepSalesRevenue > 0 && bepDenom <= 0;

    // Means of finance
    const moFinance = num(plan.own_investment) + num(plan.bank_loan) + num(plan.cef_loan) + num(plan.cif_others);
    const moFinanceGap = totalProjectCost - moFinance;

    // Proposed Loan auto-derived from Means of Finance (Bank Loan + CEF Loan).
    // Falls back to legacy proposed_loan_amount if no MoF entered yet.
    const proposedLoan = (num(plan.bank_loan) + num(plan.cef_loan)) || num(plan.proposed_loan_amount);

    return {
      productRows, totalRevenue, totalProductCost, otherIncome, totalIncome,
      totalLand, totalMachinery, totalFurniture, totalRaw, totalUtilities, totalManpower,
      totalAdmin, totalSelling, depreciation, depPlant, depFurn,
      bkValPlant, bkValFurn,
      variableCost, fixedCost, totalCostOfProduction, netProfit,
      workingCapital, workingCapitalRows, wcMonthlyOverhead, totalProjectCost,
      preOperativeExpense, preOperativeItems,
      bepSalesRevenue, bep, bepMonths, bepUnreachable,
      moFinance, moFinanceGap, proposedLoan,
    };
  }, [plan]);

  const handleSave = async () => {
    try {
      const payload = { ...plan }; delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.status;
      delete payload.approved_at; delete payload.approved_by_email; delete payload.rejection_reason; delete payload.created_by_email;
      delete payload.submitted_at; delete payload.branch_id;
      await api.put(`/business-plans/${id}`, payload);
      toast.success('Saved');
      load();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); return false; }
  };

  const handleSubmit = async () => {
    const proposedLoan = (num(plan.bank_loan) + num(plan.cef_loan)) || num(plan.proposed_loan_amount);
    if (!proposedLoan) { toast.error('Enter Bank Loan / CEF Loan amount in section 4.1 Means of Finance'); return; }
    if (!(plan.products || []).length) { toast.error('Add at least one product'); return; }
    // Validation: Section 3.4 Raw Material Total must be >= Section 2.0 Total Cost of Goods (yr).
    // (Raw material may be higher than cost-of-goods, but cannot be lower — you can't sell more than you procured.)
    if (num(calc.totalRaw) + 1 < num(calc.totalProductCost)) {
      const shortfall = num(calc.totalProductCost) - num(calc.totalRaw);
      window.alert(`The Raw Material Total must be at least equal to the Total Cost of Goods (yr).\n\nSection 2.0 — Cost of Goods (yr): ${fmtR(calc.totalProductCost)}\nSection 3.4 — Raw Material Total: ${fmtR(calc.totalRaw)}\n\nShortfall: ${fmtR(shortfall)}\n\nAdd more raw material entries or increase quantities so the Raw Material Total is greater than or equal to the Cost of Goods before submitting for approval.`);
      return;
    }
    // Auto-save current state before submitting (otherwise the in-memory edits never reach the server)
    try {
      const payload = { ...plan }; delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.status;
      delete payload.approved_at; delete payload.approved_by_email; delete payload.rejection_reason; delete payload.created_by_email;
      delete payload.submitted_at; delete payload.branch_id;
      await api.put(`/business-plans/${id}`, payload);
      await api.post(`/business-plans/${id}/submit`);
      toast.success('Saved & submitted for approval');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
  };

  const handleApprove = async () => {
    try { await api.post(`/business-plans/${id}/approve`); toast.success('Approved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const handleReject = async () => {
    try { await api.post(`/business-plans/${id}/reject`, { reason: rejectReason }); toast.success('Rejected'); setShowReject(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  // ---------- Print / PDF / Excel ----------
  const handlePrint = () => {
    if (!plan || !entrepreneur) return;
    const c = calc;
    const products = (plan.products || []).map((p, i) => {
      const r = c.productRows[i];
      return `<tr><td>${i+1}</td><td>${p.name||'-'}</td><td>${p.working_months}</td><td>${r._perDur}</td><td>${r._totalProd}</td><td>${p.utilization_pct}%</td><td>${r._perDurUtil.toFixed(2)}</td><td>${r._totalUtil.toFixed(2)}</td><td>${fmtR(p.sale_price_per_unit)}</td><td>${fmtR(r._revenue)}</td><td>${fmtR(p.cost_per_unit)}</td><td>${fmtR(r._cost)}</td></tr>`;
    }).join('');
    const rmt = (rows, cols) => `<table><thead><tr>${cols.map(c => `<th>${c}</th>`).join('')}</tr></thead><tbody>${rows.length ? rows : `<tr><td colspan="${cols.length}" style="text-align:center;color:#94a3b8">No entries</td></tr>`}</tbody></table>`;
    const lb = (plan.land_building || []).map(r => `<tr><td>${r.ownership}</td><td>${fmtR(r.amount)}</td></tr>`).join('');
    const pm = (plan.plant_machinery || []).map(r => `<tr><td>${r.particulars||'-'}</td><td>${r.nos}</td><td>${fmtR(r.unit_price)}</td><td>${r.supplier||'-'}</td><td>${r.life_years}</td><td>${fmtR(num(r.nos)*num(r.unit_price))}</td></tr>`).join('');
    const fn = (plan.furniture || []).map(r => `<tr><td>${r.particulars||'-'}</td><td>${r.nos}</td><td>${fmtR(r.unit_price)}</td><td>${r.supplier||'-'}</td><td>${r.life_years}</td><td>${fmtR(num(r.nos)*num(r.unit_price))}</td></tr>`).join('');
    const rm = (plan.raw_material || []).map(r => `<tr><td>${r.item||'-'}</td><td>${r.frequency_per_year}</td><td>${r.quantity}</td><td>${fmtR(r.unit_rate)}</td><td>${fmtR(num(r.frequency_per_year)*num(r.quantity)*num(r.unit_rate))}</td></tr>`).join('');
    const ut = (plan.utilities || []).map(r => `<tr><td>${r.type||'-'}</td><td>${fmtR(r.yearly_amount)}</td></tr>`).join('');
    const mp = (plan.manpower || []).map(r => `<tr><td>${r.role||'-'}</td><td>${fmtR(r.monthly_wage)}</td><td>${r.months}</td><td>${r.count}</td><td>${fmtR(num(r.monthly_wage)*num(r.months)*num(r.count))}</td></tr>`).join('');
    const ax = (plan.admin_expenses || []).map(r => `<tr><td>${r.type||'-'}</td><td>${fmtR(r.yearly_amount)}</td></tr>`).join('');
    const sx = (plan.selling_expenses || []).map(r => `<tr><td>${r.type||'-'}</td><td>${fmtR(r.yearly_amount)}</td></tr>`).join('');
    const wc = (plan.working_capital || []).map(r => `<tr><td>${r.type||'-'}</td><td>${r.holding_days||0}</td><td>${r.holding_qty||0}</td><td>${fmtR(r.unit_rate)}</td><td>${fmtR(num(r.holding_qty)*num(r.unit_rate))}</td><td>${r.description||'-'}</td></tr>`).join('') + `<tr style="background:#fef3c7;font-style:italic"><td>Others Exp of 1 month</td><td colspan="3" style="text-align:right;color:#64748b">auto</td><td>${fmtR(c.wcMonthlyOverhead)}</td><td>(Util+Manp+Admin+Sell)/12</td></tr>`;
    const oi = (plan.other_income || []).map(r => `<tr><td>${r.description||'-'}</td><td>${fmtR(r.amount)}</td></tr>`).join('');
    const poi = (plan.pre_operative_items || []).map(r => `<tr><td>${(r.type === 'Others' || r.type === 'Other') ? (r.type_other || 'Others') : (r.type || '-')}</td><td>${r.description||'-'}</td><td>${fmtR(r.amount)}</td></tr>`).join('');

    // --- Build inline SVG bar chart for Income vs Cost vs Profit ---
    const barItems = [
      { name: 'Income', value: Math.round(c.totalIncome || 0), color: '#10b981' },
      { name: 'Variable Cost', value: Math.round(c.variableCost || 0), color: '#f97316' },
      { name: 'Fixed Cost', value: Math.round(c.fixedCost || 0), color: '#8b5cf6' },
      { name: 'Net Profit', value: Math.round(c.netProfit || 0), color: (c.netProfit || 0) >= 0 ? '#0ea5e9' : '#ef4444' },
    ];
    const barMax = Math.max(1, ...barItems.map(b => Math.abs(b.value)));
    const chartW = 520, chartH = 180, padL = 50, padB = 28, padT = 14, padR = 10;
    const innerW = chartW - padL - padR, innerH = chartH - padT - padB;
    const barW = innerW / barItems.length * 0.6;
    const barGap = innerW / barItems.length * 0.4;
    const fmtCompact = (v) => Math.abs(v) >= 1000 ? `${(v/1000).toFixed(1)}k` : `${v}`;
    const barChartSvg = `
      <svg viewBox="0 0 ${chartW} ${chartH}" width="100%" height="${chartH}" style="font-family:Arial,sans-serif;font-size:10px">
        <line x1="${padL}" y1="${padT + innerH}" x2="${chartW - padR}" y2="${padT + innerH}" stroke="#cbd5e1"/>
        <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + innerH}" stroke="#cbd5e1"/>
        ${[0, 0.25, 0.5, 0.75, 1].map(t => {
          const y = padT + innerH - t * innerH;
          return `<line x1="${padL}" y1="${y}" x2="${chartW - padR}" y2="${y}" stroke="#e2e8f0" stroke-dasharray="2,2"/>
                  <text x="${padL - 6}" y="${y + 3}" text-anchor="end" fill="#64748b">${fmtCompact(Math.round(t * barMax))}</text>`;
        }).join('')}
        ${barItems.map((b, i) => {
          const x = padL + barGap/2 + i * (barW + barGap);
          const h = (Math.abs(b.value) / barMax) * innerH;
          const y = padT + innerH - h;
          return `<rect x="${x}" y="${y}" width="${barW}" height="${h}" fill="${b.color}" rx="3"/>
                  <text x="${x + barW/2}" y="${y - 4}" text-anchor="middle" fill="#0f172a" font-weight="700">${fmtCompact(b.value)}</text>
                  <text x="${x + barW/2}" y="${padT + innerH + 14}" text-anchor="middle" fill="#475569" font-size="10">${b.name}</text>`;
        }).join('')}
      </svg>`;

    // Cost composition horizontal stacked bar
    const costParts = [
      { name: 'Raw Material', value: Math.round(c.totalRaw || 0), color: '#0ea5e9' },
      { name: 'Manpower', value: Math.round(c.totalManpower || 0), color: '#10b981' },
      { name: 'Utilities', value: Math.round(c.totalUtilities || 0), color: '#f59e0b' },
      { name: 'Admin', value: Math.round(c.totalAdmin || 0), color: '#8b5cf6' },
      { name: 'Selling', value: Math.round(c.totalSelling || 0), color: '#ef4444' },
      { name: 'Depreciation', value: Math.round(c.depreciation || 0), color: '#64748b' },
    ].filter(d => d.value > 0);
    const costTotal = Math.max(1, costParts.reduce((s, p) => s + p.value, 0));
    let cumPct = 0;
    const stackedBar = `
      <div style="display:flex;height:24px;border-radius:4px;overflow:hidden;border:1px solid #cbd5e1;margin:6px 0">
        ${costParts.map(p => {
          const pct = (p.value / costTotal) * 100;
          cumPct += pct;
          return `<div style="background:${p.color};width:${pct}%;color:#fff;font-size:9px;display:flex;align-items:center;justify-content:center;font-weight:700">${pct >= 8 ? `${pct.toFixed(0)}%` : ''}</div>`;
        }).join('')}
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:10px;font-size:10px;color:#475569">
        ${costParts.map(p => `<span style="display:inline-flex;align-items:center;gap:4px"><i style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px"></i> ${p.name} — <b style="color:#0f172a">${fmtR(p.value)}</b></span>`).join('')}
      </div>`;

    const html = `<!doctype html><html><head><title>Business Plan — ${entrepreneur.name}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:'Helvetica Neue',Arial,sans-serif;padding:0;margin:0;color:#0f172a;background:#fff}
      .page{padding:18mm 14mm}
      /* Letterhead */
      .letterhead{position:relative;padding:14px 18px 16px;background:linear-gradient(135deg,#065f46 0%,#047857 60%,#10b981 100%);color:#fff;border-radius:10px;overflow:hidden;margin-bottom:16px}
      .letterhead::after{content:'';position:absolute;right:-40px;top:-40px;width:160px;height:160px;background:rgba(255,255,255,0.08);border-radius:50%}
      .letterhead::before{content:'';position:absolute;left:-30px;bottom:-50px;width:120px;height:120px;background:rgba(255,255,255,0.06);border-radius:50%}
      .lh-row{display:flex;justify-content:space-between;align-items:center;position:relative;z-index:1}
      .lh-org{font-size:11px;letter-spacing:2px;text-transform:uppercase;opacity:.85;font-weight:600}
      .lh-title{font-size:22px;font-weight:800;margin:2px 0 4px;letter-spacing:.3px}
      .lh-sub{font-size:11px;opacity:.85}
      .lh-status{padding:4px 10px;background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.4);border-radius:20px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;backdrop-filter:blur(4px)}
      .lh-meta{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:12px;font-size:10px;position:relative;z-index:1}
      .lh-meta b{display:block;opacity:.75;font-weight:600;letter-spacing:.5px;text-transform:uppercase;font-size:9px;margin-bottom:1px}
      .lh-meta span{font-weight:700;font-size:11px}
      /* Section heading */
      h2{color:#047857;font-size:13px;border-bottom:2px solid #047857;padding-bottom:4px;margin:18px 0 8px;text-transform:uppercase;letter-spacing:.5px}
      h3{font-size:12px;margin:10px 0 4px;color:#334155;font-weight:700}
      /* Tables */
      table{width:100%;border-collapse:collapse;font-size:10.5px;margin-bottom:8px}
      th{background:#047857;color:#fff;padding:6px 8px;text-align:left;font-weight:600;font-size:10px;letter-spacing:.3px}
      td{padding:5px 8px;border-bottom:1px solid #e2e8f0}
      tbody tr:nth-child(even) td{background:#f8fafc}
      .info-table th{background:#ecfdf5;color:#065f46;width:40%;font-size:11px}
      .info-table td{font-size:11px}
      .grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
      /* KPI grid (Project Summary) */
      .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:6px 0 14px}
      .kpi{padding:9px 11px;border-radius:8px;border:1px solid;background:#fff;position:relative;overflow:hidden}
      .kpi small{display:block;font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;font-weight:600;margin-bottom:2px}
      .kpi b{font-size:13.5px;color:#0f172a;display:block;font-weight:800;line-height:1.2}
      .kpi-em{border-color:#86efac;background:linear-gradient(135deg,#f0fdf4,#dcfce7)}
      .kpi-rs{border-color:#fecaca;background:linear-gradient(135deg,#fef2f2,#fee2e2)}
      .kpi-bl{border-color:#bfdbfe;background:linear-gradient(135deg,#eff6ff,#dbeafe)}
      .kpi-pp{border-color:#ddd6fe;background:linear-gradient(135deg,#faf5ff,#ede9fe)}
      .kpi-am{border-color:#fde68a;background:linear-gradient(135deg,#fffbeb,#fef3c7)}
      .kpi-in{border-color:#c7d2fe;background:linear-gradient(135deg,#eef2ff,#e0e7ff)}
      .kpi-sl{border-color:#cbd5e1;background:linear-gradient(135deg,#f8fafc,#f1f5f9)}
      /* Charts */
      .chart-card{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;background:#fff;margin-bottom:10px}
      .chart-title{font-size:11px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
      .chart-row{display:grid;grid-template-columns:1.4fr 1fr;gap:14px;margin:8px 0 14px}
      /* Health badge & exec summary */
      .summary-box{background:linear-gradient(135deg,#f0fdf4,#ecfdf5);border:1px solid #86efac;border-radius:10px;padding:12px 14px;margin:0 0 14px}
      .summary-title{margin:0 0 8px;color:#047857;font-size:13px;letter-spacing:.5px;text-transform:uppercase;font-weight:800;display:flex;align-items:center;gap:6px}
      /* Signatures */
      .signatures{margin-top:36px;padding-top:18px;border-top:2px solid #cbd5e1;page-break-inside:avoid}
      .sig-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:32px;margin-top:14px}
      .sig-block{font-size:11px}
      .sig-line{border-bottom:1.5px solid #1e293b;height:50px;margin-bottom:6px}
      .sig-label{color:#475569;font-size:10px;text-transform:uppercase;letter-spacing:.5px;font-weight:600}
      .sig-name{font-weight:700;color:#0f172a}
      .approved-stamp{margin-top:14px;padding:10px;background:#dcfce7;border:2px solid #16a34a;color:#15803d;font-weight:bold;text-align:center;border-radius:6px;letter-spacing:1px}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 portrait;margin:0}.page{padding:14mm}}
    </style></head><body>
    <div class="page">
      <!-- LETTERHEAD -->
      <div class="letterhead">
        <div class="lh-row">
          <div>
            <div class="lh-org">Wangjing Women and Girls Society — BRC Portal</div>
            <div class="lh-title">${(entrepreneur.enterprise?.name||'').toUpperCase() || 'BUSINESS PLAN'}</div>
            <div class="lh-sub">Business Plan &amp; Financial Projection · Prepared for ${entrepreneur.name || '—'}</div>
          </div>
          <span class="lh-status">${(plan.status||'draft').replace('_',' ')}</span>
        </div>
        <div class="lh-meta">
          <div><b>Plan Date</b><span>${plan.plan_date ? new Date(plan.plan_date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</span></div>
          <div><b>Enrollment</b><span>${entrepreneur.enrollment_no || '—'}</span></div>
          <div><b>Branch</b><span>${branch?.name || '—'}</span></div>
          <div><b>CRP-EP / Mentor</b><span>${plan.crp_ep_name || entrepreneur.crp_ep_name || '—'}</span></div>
        </div>
      </div>

      <!-- PROJECT SUMMARY (KPI cards) -->
      <h2 style="margin-top:0">Project Summary — Executive Snapshot</h2>
      <div class="kpi-grid">
        <div class="kpi kpi-em"><small>Total Income (yearly)</small><b>${fmtR(c.totalIncome)}</b></div>
        <div class="kpi kpi-rs"><small>Total Cost of Production</small><b>${fmtR(c.totalCostOfProduction)}</b></div>
        <div class="kpi kpi-em"><small>Net Profit (yearly)</small><b style="color:${c.netProfit>=0?'#047857':'#b91c1c'}">${fmtR(c.netProfit)}</b></div>
        <div class="kpi ${c.totalIncome>0 && (c.netProfit/c.totalIncome)*100 >= 15 ? 'kpi-em' : (c.totalIncome>0 && (c.netProfit/c.totalIncome)*100 >= 5 ? 'kpi-am' : 'kpi-rs')}"><small>Profit Margin</small><b>${c.totalIncome>0?((c.netProfit/c.totalIncome)*100).toFixed(1):'0'}%</b></div>
        <div class="kpi kpi-in"><small>Total Project Cost</small><b>${fmtR(c.totalProjectCost)}</b></div>
        <div class="kpi kpi-bl"><small>Proposed Loan</small><b>${fmtR(c.proposedLoan)}</b></div>
        <div class="kpi kpi-em"><small>Net Profit / Month</small><b>${fmtR((c.netProfit||0)/12)}</b></div>
        <div class="kpi kpi-pp"><small>Break-even Point (₹)</small><b>${c.bepUnreachable?'— Not reachable':fmtR(c.bep)}</b></div>
        <div class="kpi kpi-pp"><small>BEP Period</small><b>${c.bepUnreachable?'— Not reachable':`${(c.bepMonths||0).toFixed(1)} mo`}</b></div>
        <div class="kpi ${(c.netProfit||0)>0 && (c.proposedLoan/((c.netProfit||0)/12)) <= (plan.repayment_period_months||24) ? 'kpi-em' : 'kpi-am'}"><small>Loan Recovery Period</small><b>${(c.netProfit||0)>0?`${(c.proposedLoan/((c.netProfit||0)/12)).toFixed(1)} mo`:'—'}</b></div>
        <div class="kpi kpi-sl"><small>Repayment Tenure</small><b>${plan.repayment_period_months||0} mo</b></div>
        <div class="kpi kpi-sl"><small>Working Capital</small><b>${fmtR(c.workingCapital)}</b></div>
      </div>

      <!-- VISUAL CHARTS ROW -->
      <div class="chart-row">
        <div class="chart-card">
          <div class="chart-title">Income vs Cost vs Profit (Yearly)</div>
          ${barChartSvg}
        </div>
        <div class="chart-card">
          <div class="chart-title">Cost Composition</div>
          ${costParts.length === 0 ? `<div style="padding:14px;text-align:center;color:#94a3b8;font-size:11px">No cost data entered yet</div>` : stackedBar}
        </div>
      </div>

      <h2>1.0 Information of Enterprise</h2>
      <div class="grid2">
        <table class="info-table">
          <tr><th>Name of the Applicant</th><td>${entrepreneur.name||'—'}</td></tr>
          <tr><th>Mobile</th><td>${entrepreneur.mobile||'—'}</td></tr>
          <tr><th>Aadhaar</th><td>${entrepreneur.aadhaar_number||'—'}</td></tr>
          <tr><th>PAN</th><td>${entrepreneur.pan_number||'—'}</td></tr>
          <tr><th>Gender</th><td>${entrepreneur.gender||'—'}</td></tr>
          <tr><th>Caste</th><td>${entrepreneur.caste||'—'}</td></tr>
          <tr><th>State</th><td>${entrepreneur.state||'—'}</td></tr>
          <tr><th>District</th><td>${entrepreneur.district||'—'}</td></tr>
          <tr><th>Block</th><td>${entrepreneur.block||'—'}</td></tr>
          <tr><th>Gram Panchayat</th><td>${entrepreneur.gram_panchayat||'—'}</td></tr>
          <tr><th>Village</th><td>${entrepreneur.village||'—'}</td></tr>
          <tr><th>Pincode</th><td>${entrepreneur.pincode||'—'}</td></tr>
          <tr><th>Address of Unit</th><td>${entrepreneur.address||'—'}</td></tr>
        </table>
        <table class="info-table">
          <tr><th>Name of Unit (Enterprise)</th><td>${entrepreneur.enterprise?.name||'—'}</td></tr>
          <tr><th>Type of Operation</th><td>${entrepreneur.enterprise?.operation_type||'—'}</td></tr>
          <tr><th>Sector</th><td>${entrepreneur.enterprise?.type||'—'}</td></tr>
          <tr><th>Product / Item</th><td>${entrepreneur.enterprise?.product_item||'—'}</td></tr>
          <tr><th>Ownership Structure</th><td>${entrepreneur.enterprise?.ownership_structure||'—'}</td></tr>
          <tr><th>Govt. License Available</th><td>${entrepreneur.enterprise?.govt_license||'—'}</td></tr>
          <tr><th>Enterprise Type</th><td>${entrepreneur.enterprise?.is_existing ? 'EXISTING' : 'NEW'}</td></tr>
          <tr><th>SHG / VLF</th><td>${entrepreneur.shg_vlf_name||'—'}</td></tr>
          <tr><th>SHG Member</th><td>${entrepreneur.shg_member_name||'—'} (${entrepreneur.shg_member_relation||'—'})</td></tr>
          <tr><th>CRP-EP / Mentor</th><td>${plan.crp_ep_name || entrepreneur.crp_ep_name||'—'}</td></tr>
          <tr><th>Loan Type</th><td>${entrepreneur.enterprise?.loan_type||'—'}</td></tr>
          <tr><th>Proposed Loan Amount</th><td><b>${fmtR(c.proposedLoan)}</b></td></tr>
          <tr><th>Repayment Period</th><td>${plan.repayment_period_months} months</td></tr>
        </table>
      </div>

      <h2>2.0 Production &amp; Revenue Planning (for 1 year)</h2>
      <table><thead><tr><th>#</th><th>Product Name</th><th>Working Duration</th><th>Per Dura. Prod./Cust.</th><th>Total Produ.</th><th>Utili (%)</th><th>Per Dura. Utili.</th><th>Total Utilization</th><th>Sales Price / Unit</th><th>Sales Revenue</th><th>Cost Price (BoM)</th><th>Cost of Goods Used</th></tr></thead><tbody>${products||'<tr><td colspan="12" style="text-align:center;color:#94a3b8">No products</td></tr>'}<tr style="background:#ecfdf5;font-weight:bold"><td colspan="9">TOTAL</td><td>${fmtR(c.totalRevenue)}</td><td></td><td>${fmtR(c.totalProductCost)}</td></tr></tbody></table>
      <h2>2.1 Other Income</h2>${rmt(oi, ['Description','Amount'])}

      <h2>3.0 Annual Expenditure</h2>
      <h3 style="font-size:12px;margin:8px 0 4px">3.0 Pre-operative Expenses</h3>${(plan.pre_operative_items||[]).length > 0 ? `<table><thead><tr><th>Category</th><th>Description</th><th>Amount</th></tr></thead><tbody>${poi}<tr style="background:#ecfdf5;font-weight:bold"><td colspan="2">TOTAL</td><td>${fmtR(c.preOperativeExpense)}</td></tr></tbody></table>` : `<table><tr><td>Pre-operative Expense</td><td style="text-align:right">${fmtR(c.preOperativeExpense)}</td></tr></table>`}
      <h3 style="font-size:12px;margin:8px 0 4px">3.1 Land &amp; Building</h3>${rmt(lb, ['Ownership','Amount'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.2 Plant / Machinery</h3>${rmt(pm, ['Particulars','Nos','Unit Price','Supplier','Life (yrs)','Total'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.3 Furniture</h3>${rmt(fn, ['Particulars','Nos','Unit Price','Supplier','Life (yrs)','Total'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.4 Raw Material</h3>${rmt(rm, ['Item','Freq/Yr','Quantity','Unit Rate','Total'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.5 Utilities</h3>${rmt(ut, ['Utility','Yearly Amount'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.6 Manpower</h3>${rmt(mp, ['Role','Monthly Wage','Months','Count','Total'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.7 Administrative</h3>${rmt(ax, ['Type','Yearly Amount'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.8 Selling &amp; Distribution</h3>${rmt(sx, ['Type','Yearly Amount'])}
      <h3 style="font-size:12px;margin:8px 0 4px">3.9 Working Capital</h3><table><thead><tr><th>Type</th><th>Days</th><th>Qty</th><th>Unit Rate</th><th>Inventory Cost</th><th>Description</th></tr></thead><tbody>${wc}<tr style="background:#ecfdf5;font-weight:bold"><td colspan="4">Total Working Capital</td><td>${fmtR(c.workingCapital)}</td><td></td></tr></tbody></table>

      <h2>3.10 Total Project Cost</h2>
      <table>
        <tr><th>Pre-operative</th><td>${fmtR(c.preOperativeExpense)}</td></tr>
        <tr><th>Land &amp; Building</th><td>${fmtR(c.totalLand)}</td></tr>
        <tr><th>Plant / Machinery</th><td>${fmtR(c.totalMachinery)}</td></tr>
        <tr><th>Furniture</th><td>${fmtR(c.totalFurniture)}</td></tr>
        <tr><th>Working Capital</th><td>${fmtR(c.workingCapital)}</td></tr>
        <tr style="background:#ecfdf5;font-weight:bold"><th>TOTAL PROJECT COST</th><td>${fmtR(c.totalProjectCost)}</td></tr>
      </table>

      <h2>4.1 Profitability Projection</h2>
      <table>
        <tr><th>A. Total Income</th><td>${fmtR(c.totalIncome)}</td></tr>
        <tr><th>i. Raw Material</th><td>${fmtR(c.totalRaw)}</td></tr>
        <tr><th>ii. Utilities</th><td>${fmtR(c.totalUtilities)}</td></tr>
        <tr><th>iii. Manpower</th><td>${fmtR(c.totalManpower)}</td></tr>
        <tr><th>iv. Administrative</th><td>${fmtR(c.totalAdmin)}</td></tr>
        <tr><th>v. Selling &amp; Distribution</th><td>${fmtR(c.totalSelling)}</td></tr>
        <tr><th>vi. Pre-loan Interest</th><td>${fmtR(plan.pre_loan_interest)}</td></tr>
        <tr><th>B. Total Cost of Production</th><td>${fmtR(c.totalCostOfProduction)}</td></tr>
        <tr><th>C. Depreciation</th><td>${fmtR(c.depreciation)}</td></tr>
        <tr style="background:${c.netProfit>=0?'#ecfdf5':'#fff1f2'};font-weight:bold"><th>D. Net Profit (A − B − C)</th><td>${fmtR(c.netProfit)}</td></tr>
      </table>

      <h2>4.2 Break-even Analysis</h2>
      <div class="kpis">
        <div class="kpi"><small>Sales Revenue</small><b>${fmtR(c.bepSalesRevenue)}</b></div>
        <div class="kpi"><small>Fixed Cost</small><b>${fmtR(c.fixedCost)}</b></div>
        <div class="kpi"><small>Variable Cost</small><b>${fmtR(c.variableCost)}</b></div>
        <div class="kpi"><small>Break-even Point</small><b>${fmtR(c.bep)}</b></div>
        <div class="kpi"><small>BEP Period</small><b>${c.bepMonths.toFixed(1)} months</b></div>
      </div>

      <h2>4.3 Means of Finance</h2>
      <table>
        <tr><th>Own Investment</th><td>${fmtR(plan.own_investment)}</td></tr>
        <tr><th>Bank Loan</th><td>${fmtR(plan.bank_loan)}</td></tr>
        <tr><th>CEF Loan</th><td>${fmtR(plan.cef_loan)}</td></tr>
        <tr><th>CIF / Others</th><td>${fmtR(plan.cif_others)}</td></tr>
        <tr style="background:#ecfdf5;font-weight:bold"><th>TOTAL FUND ARRANGED</th><td>${fmtR(c.moFinance)}</td></tr>
        <tr style="background:${c.moFinanceGap===0?'#ecfdf5':'#fff7ed'}"><th>Gap (Project Cost − Fund Arranged)</th><td>${fmtR(c.moFinanceGap)}</td></tr>
      </table>

      <!-- Officers Involved + Signature Block -->
      <div class="signatures">
        <h2 style="margin-bottom:10px">Workflow &amp; Approval</h2>
        <table>
          <thead><tr><th style="width:30%">Role</th><th>Officer</th><th style="width:25%">Status / Date</th></tr></thead>
          <tbody>
            <tr>
              <td><b>Prepared by (Mentor / CRP-EP)</b></td>
              <td>${plan.created_by_email || entrepreneur.crp_ep_name || '—'}</td>
              <td>${plan.created_at ? new Date(plan.created_at).toLocaleDateString('en-IN') : '—'}</td>
            </tr>
            <tr>
              <td><b>Submitted by</b></td>
              <td>${plan.submitted_by_email || plan.created_by_email || '—'}</td>
              <td>${plan.submitted_at ? new Date(plan.submitted_at).toLocaleDateString('en-IN') : (plan.status==='draft'?'Pending':'—')}</td>
            </tr>
            <tr>
              <td><b>Reviewed / Checked</b></td>
              <td>${plan.checked_by_email || '—'}</td>
              <td>${plan.checked_at ? new Date(plan.checked_at).toLocaleDateString('en-IN') : '—'}</td>
            </tr>
            <tr>
              <td><b>Approved by (Admin)</b></td>
              <td>${plan.approved_by_email || '—'}</td>
              <td>${plan.approved_at ? new Date(plan.approved_at).toLocaleDateString('en-IN') : (plan.status==='approved'?'Approved':'Pending')}</td>
            </tr>
            ${plan.rejection_reason ? `<tr><td><b>Rejected by</b></td><td>${plan.rejected_by_email||'—'}</td><td>${plan.rejected_at?new Date(plan.rejected_at).toLocaleDateString('en-IN'):'—'} — Reason: ${plan.rejection_reason}</td></tr>` : ''}
          </tbody>
        </table>

        <h3 style="margin-top:18px">Signatures</h3>
        <div class="sig-grid">
          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-label">Mentor / CRP-EP (Prepared by)</div>
            <div class="sig-name">${entrepreneur.crp_ep_name || plan.created_by_email || ''}</div>
          </div>
          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-label">Branch Manager / Reviewer</div>
            <div class="sig-name">${plan.checked_by_email || ''}</div>
          </div>
          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-label">Approver (Admin)</div>
            <div class="sig-name">${plan.approved_by_email || ''}</div>
          </div>
          <div class="sig-block">
            <div class="sig-line"></div>
            <div class="sig-label">Applicant (Entrepreneur)</div>
            <div class="sig-name">${entrepreneur.name || ''}</div>
          </div>
        </div>
        ${plan.status === 'approved' ? `<div class="approved-stamp">✓ APPROVED — Plan Sanctioned${plan.approved_at?` on ${new Date(plan.approved_at).toLocaleDateString('en-IN')}`:''}</div>` : ''}
      </div>
    </div>
    </body></html>`;
    const w = window.open('', '_blank'); if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  const handleExcel = () => {
    if (!plan || !entrepreneur) return;
    const c = calc;
    const sheets = [
      { name: 'Summary', rows: [
        { Field: 'Entrepreneur', Value: entrepreneur.name },
        { Field: 'Enrollment', Value: entrepreneur.enrollment_no || '' },
        { Field: 'Branch', Value: branch?.name || '' },
        { Field: 'Plan Date', Value: plan.plan_date || '' },
        { Field: 'Status', Value: plan.status },
        { Field: 'Proposed Loan', Value: Math.round(c.proposedLoan) },
        { Field: 'Total Income', Value: Math.round(c.totalIncome) },
        { Field: 'Total Cost of Production', Value: Math.round(c.totalCostOfProduction) },
        { Field: 'Depreciation', Value: Math.round(c.depreciation) },
        { Field: 'Net Profit', Value: Math.round(c.netProfit) },
        { Field: 'Total Project Cost', Value: Math.round(c.totalProjectCost) },
        { Field: 'Working Capital', Value: Math.round(c.workingCapital) },
        { Field: 'Break-even Point', Value: Math.round(c.bep) },
        { Field: 'BEP Months', Value: Number(c.bepMonths.toFixed(2)) },
        { Field: 'Means of Finance', Value: Math.round(c.moFinance) },
      ]},
      { name: 'Products', rows: c.productRows.map(p => ({ Product: p.name, Months: p.working_months, 'Util %': p.utilization_pct, 'Qty/Yr': p.qty_per_year, 'Sale Price': p.sale_price_per_unit, 'Cost': p.cost_per_unit, Revenue: Math.round(p._revenue), 'Total Cost': Math.round(p._cost), Profit: Math.round(p._profit) })) },
      { name: 'Plant Machinery', rows: (plan.plant_machinery||[]).map(r => {
        const total = num(r.nos)*num(r.unit_price);
        const dep = Math.max(0, (total - num(r.scrap_value)) / (num(r.life_years) || 1));
        return { Particulars: r.particulars, Nos: r.nos, 'Unit Price': r.unit_price, Total: total, 'Scrap Value': num(r.scrap_value), 'Life (yrs)': r.life_years, 'Depreciation/yr': Math.round(dep), 'Book Value': Math.round(total - dep), Supplier: r.supplier };
      }) },
      { name: 'Furniture', rows: (plan.furniture||[]).map(r => {
        const total = num(r.nos)*num(r.unit_price);
        const dep = Math.max(0, (total - num(r.scrap_value)) / (num(r.life_years) || 1));
        return { Particulars: r.particulars, Nos: r.nos, 'Unit Price': r.unit_price, Total: total, 'Scrap Value': num(r.scrap_value), 'Life (yrs)': r.life_years, 'Depreciation/yr': Math.round(dep), 'Book Value': Math.round(total - dep), Supplier: r.supplier };
      }) },
      { name: 'Raw Material', rows: (plan.raw_material||[]).map(r => ({ Item: r.item, 'Freq/Yr': r.frequency_per_year, Quantity: r.quantity, 'Unit Rate': r.unit_rate, Total: num(r.frequency_per_year)*num(r.quantity)*num(r.unit_rate) })) },
      { name: 'Manpower', rows: (plan.manpower||[]).map(r => ({ Role: r.role, 'Monthly Wage': r.monthly_wage, Months: r.months, Count: r.count, Total: num(r.monthly_wage)*num(r.months)*num(r.count) })) },
      { name: 'Utilities', rows: (plan.utilities||[]).map(r => ({ Utility: r.type, Yearly: r.yearly_amount })) },
      { name: 'Admin', rows: (plan.admin_expenses||[]).map(r => ({ Type: r.type, Yearly: r.yearly_amount })) },
      { name: 'Selling', rows: (plan.selling_expenses||[]).map(r => ({ Type: r.type, Yearly: r.yearly_amount })) },
      { name: 'Pre-operative', rows: [
        ...((plan.pre_operative_items||[]).map(r => ({
          Category: r.type === 'Others' || r.type === 'Other' ? (r.type_other || 'Others') : (r.type || '-'),
          Description: r.description || '',
          Amount: num(r.amount),
        }))),
        { Category: 'TOTAL', Description: '', Amount: Math.round(c.preOperativeExpense) },
      ] },
      { name: 'Working Capital', rows: [
        ...(plan.working_capital||[]).map(r => ({ Type: r.type, 'Days': r.holding_days, 'Qty': r.holding_qty, 'Unit Rate': r.unit_rate, 'Inventory Cost': num(r.holding_qty)*num(r.unit_rate), Description: r.description })),
        { Type: 'Others Exp of 1 month (auto)', Days: '', Qty: '', 'Unit Rate': '', 'Inventory Cost': Math.round(c.wcMonthlyOverhead), Description: '(Utilities+Manpower+Admin+Selling)/12' },
        { Type: 'TOTAL', Days: '', Qty: '', 'Unit Rate': '', 'Inventory Cost': Math.round(c.workingCapital), Description: '' },
      ] },
    ];
    exportToExcel({ filename: `WWAGS_BusinessPlan_${(entrepreneur.name||'plan').replace(/\s+/g,'_')}`, sheets });
  };

  const handlePDF = () => {
    if (!plan || !entrepreneur) return;
    const c = calc;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const M = 12; // margin
    const pageW = doc.internal.pageSize.getWidth();
    let y = M;

    // -------- HEADER --------
    doc.setFillColor(4, 120, 87);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setFontSize(16); doc.setTextColor(255);
    doc.text((entrepreneur.enterprise?.name || 'BUSINESS PLAN').toUpperCase(), pageW / 2, 11, { align: 'center' });
    doc.setFontSize(8.5); doc.setTextColor(220, 252, 231);
    doc.text('Business Plan & Financial Projection', pageW / 2, 17, { align: 'center' });
    y = 28;
    doc.setFontSize(8); doc.setTextColor(80);
    doc.text(`Plan Date: ${plan.plan_date ? new Date(plan.plan_date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}    |    Enrollment: ${entrepreneur.enrollment_no || '—'}    |    Branch: ${branch?.name || '—'}    |    Status: ${(plan.status || '').replace('_', ' ').toUpperCase()}`, M, y);
    y += 6;

    // -------- EXECUTIVE SUMMARY --------
    doc.setFontSize(11); doc.setTextColor(4, 120, 87);
    doc.text('📊 EXECUTIVE SUMMARY', M, y); y += 2;
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value', 'Metric', 'Value']],
      body: [
        ['Total Income (yearly)', fmtR(c.totalIncome), 'Total Cost of Production', fmtR(c.totalCostOfProduction)],
        ['Net Profit (yearly)', fmtR(c.netProfit), 'Profit Margin', `${c.totalIncome > 0 ? ((c.netProfit / c.totalIncome) * 100).toFixed(1) : '0'}%`],
        ['Total Project Cost', fmtR(c.totalProjectCost), 'Proposed Loan', fmtR(c.proposedLoan)],
        ['Net Profit / Month', fmtR((c.netProfit || 0) / 12), 'BEP (Break-even Rs.)', c.bepUnreachable ? '— Not reachable' : fmtR(c.bep)],
        ['BEP Period', c.bepUnreachable ? '— Not reachable' : `${(c.bepMonths || 0).toFixed(1)} months`, 'Loan Recovery Period', (c.netProfit || 0) > 0 ? `${(plan.proposed_loan_amount / ((c.netProfit || 0) / 12)).toFixed(1)} months` : '—'],
        ['Repayment Tenure', `${plan.repayment_period_months || 0} months`, 'Working Capital', fmtR(c.workingCapital)],
      ],
      styles: { fontSize: 8.5, cellPadding: 2 },
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [71, 85, 105] }, 2: { fontStyle: 'bold', textColor: [71, 85, 105] } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;

    // -------- ENTERPRISE INFORMATION --------
    doc.setFontSize(10); doc.setTextColor(4, 120, 87);
    doc.text('1.0 INFORMATION OF ENTERPRISE', M, y); y += 2;
    autoTable(doc, {
      startY: y,
      body: [
        ['Applicant Name', entrepreneur.name || '—', 'Enterprise', entrepreneur.enterprise?.name || '—'],
        ['Mobile', entrepreneur.mobile || '—', 'Sector', entrepreneur.enterprise?.type || '—'],
        ['Aadhaar', entrepreneur.aadhaar_number || '—', 'Operation Type', entrepreneur.enterprise?.operation_type || '—'],
        ['PAN', entrepreneur.pan_number || '—', 'Product / Item', entrepreneur.enterprise?.product_item || '—'],
        ['Gender', entrepreneur.gender || '—', 'Ownership Structure', entrepreneur.enterprise?.ownership_structure || '—'],
        ['Caste', entrepreneur.caste || '—', 'Govt License', entrepreneur.enterprise?.govt_license || '—'],
        ['State', entrepreneur.state || '—', 'Enterprise Type', entrepreneur.enterprise?.is_existing ? 'EXISTING' : 'NEW'],
        ['District', entrepreneur.district || '—', 'SHG / VLF', entrepreneur.shg_vlf_name || '—'],
        ['Block', entrepreneur.block || '—', 'SHG Member', `${entrepreneur.shg_member_name || '—'} (${entrepreneur.shg_member_relation || '—'})`],
        ['Gram Panchayat', entrepreneur.gram_panchayat || '—', 'CRP-EP / Mentor', plan.crp_ep_name || entrepreneur.crp_ep_name || '—'],
        ['Village', entrepreneur.village || '—', 'Loan Type', entrepreneur.enterprise?.loan_type || '—'],
        ['Pincode', entrepreneur.pincode || '—', 'Repayment Period', `${plan.repayment_period_months} months`],
        ['Address of Unit', entrepreneur.address || '—', 'Proposed Loan', fmtR(c.proposedLoan)],
      ],
      styles: { fontSize: 8, cellPadding: 1.8 },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [71, 85, 105], cellWidth: 38 }, 2: { fontStyle: 'bold', textColor: [71, 85, 105], cellWidth: 38 } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;

    // -------- SECTION 2.0 PRODUCTS --------
    if ((plan.products || []).length > 0) {
      doc.setFontSize(10); doc.setTextColor(4, 120, 87);
      doc.text('2.0 PRODUCTION & REVENUE PLANNING', M, y); y += 2;
      autoTable(doc, {
        startY: y,
        head: [['#', 'Product', 'Months', 'Per Dura. Prod.', 'Total Prod.', 'Util %', 'Total Util.', 'Sale Rs.', 'Revenue', 'Cost Rs.', 'Total Cost']],
        body: c.productRows.map((p, i) => [
          i + 1,
          p.name || '',
          p.working_months || 0,
          p.prod_per_duration || 0,
          ((p.working_months || 0) * (p.prod_per_duration || 0)).toFixed(0),
          `${p.utilization_pct || 0}%`,
          (p._utilizedQty || 0).toFixed(0),
          fmtR(p.sale_price_per_unit),
          fmtR(p._revenue),
          fmtR(p.cost_per_unit),
          fmtR(p._cost),
        ]),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [4, 120, 87], textColor: 255, fontSize: 7.5 },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    // Helper for capital sections
    const sec = (title, head, rows) => {
      if (!rows || rows.length === 0) return;
      doc.setFontSize(10); doc.setTextColor(4, 120, 87);
      if (y > 250) { doc.addPage(); y = M; }
      doc.text(title, M, y); y += 2;
      autoTable(doc, {
        startY: y, head: [head], body: rows,
        styles: { fontSize: 8, cellPadding: 1.6 },
        headStyles: { fillColor: [4, 120, 87], textColor: 255, fontSize: 8 },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 5;
    };

    if ((plan.pre_operative_items || []).length > 0) {
      sec('3.0 PRE-OPERATIVE EXPENSES', ['Category', 'Description', 'Amount'],
        [
          ...(plan.pre_operative_items || []).map(r => [
            (r.type === 'Others' || r.type === 'Other') ? (r.type_other || 'Others') : (r.type || '-'),
            r.description || '-',
            fmtR(r.amount),
          ]),
          [{ content: 'TOTAL', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [220, 252, 231] } }, { content: fmtR(c.preOperativeExpense), styles: { fontStyle: 'bold', fillColor: [220, 252, 231] } }],
        ]);
    }
    sec('3.1 LAND & BUILDING', ['Description', 'Amount'],
      (plan.land_building || []).map(r => [r.description || '-', fmtR(r.amount)]));
    sec('3.2 PLANT & MACHINERY', ['Item', 'Nos', 'Unit Price', 'Total', 'Life (yr)'],
      (plan.plant_machinery || []).map(r => [r.item || '-', r.nos || 0, fmtR(r.unit_price), fmtR((r.nos || 0) * (r.unit_price || 0)), r.life_years || 0]));
    sec('3.3 FURNITURE & FIXTURES', ['Item', 'Nos', 'Unit Price', 'Total'],
      (plan.furniture || []).map(r => [r.item || '-', r.nos || 0, fmtR(r.unit_price), fmtR((r.nos || 0) * (r.unit_price || 0))]));
    sec('3.4 RAW MATERIAL', ['Material', 'Freq/Yr', 'Qty', 'Unit Rate', 'Total'],
      (plan.raw_material || []).map(r => [r.name || '-', r.frequency_per_year || 0, r.quantity || 0, fmtR(r.unit_rate), fmtR((r.frequency_per_year || 0) * (r.quantity || 0) * (r.unit_rate || 0))]));
    sec('3.5 UTILITIES', ['Utility', 'Yearly Amount'],
      (plan.utilities || []).map(r => [r.type || '-', fmtR(r.yearly_amount)]));
    sec('3.6 MANPOWER', ['Position', 'Count', 'Monthly Wage', 'Months', 'Total'],
      (plan.manpower || []).map(r => [r.position || '-', r.count || 0, fmtR(r.monthly_wage), r.months || 0, fmtR((r.count || 0) * (r.monthly_wage || 0) * (r.months || 0))]));
    sec('3.7 ADMIN EXPENSES', ['Type', 'Yearly Amount'],
      (plan.admin_expenses || []).map(r => [r.type || '-', fmtR(r.yearly_amount)]));
    sec('3.8 SELLING & DISTRIBUTION', ['Type', 'Yearly Amount'],
      (plan.selling_expenses || []).map(r => [r.type || '-', fmtR(r.yearly_amount)]));

    // 3.9 Working Capital with auto row
    if ((plan.working_capital || []).length > 0) {
      doc.setFontSize(10); doc.setTextColor(4, 120, 87);
      if (y > 250) { doc.addPage(); y = M; }
      doc.text('3.9 WORKING CAPITAL', M, y); y += 2;
      autoTable(doc, {
        startY: y,
        head: [['Type', 'Days', 'Qty', 'Unit Rate', 'Inventory Cost', 'Description']],
        body: [
          ...(plan.working_capital || []).map(r => [r.type || '-', r.holding_days || 0, r.holding_qty || 0, fmtR(r.unit_rate), fmtR((r.holding_qty || 0) * (r.unit_rate || 0)), r.description || '-']),
          ['Others Exp of 1 month (auto)', '—', '—', '—', fmtR(c.wcMonthlyOverhead), '(Util+Manp+Admin+Sell)/12'],
          [{ content: 'TOTAL WORKING CAPITAL', colSpan: 4, styles: { fontStyle: 'bold', halign: 'right', fillColor: [220, 252, 231] } }, { content: fmtR(c.workingCapital), styles: { fontStyle: 'bold', fillColor: [220, 252, 231] } }, ''],
        ],
        styles: { fontSize: 8, cellPadding: 1.6 },
        headStyles: { fillColor: [4, 120, 87], textColor: 255 },
        margin: { left: M, right: M },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    // -------- TOTAL PROJECT COST --------
    doc.setFontSize(10); doc.setTextColor(4, 120, 87);
    if (y > 250) { doc.addPage(); y = M; }
    doc.text('3.10 TOTAL PROJECT COST', M, y); y += 2;
    autoTable(doc, {
      startY: y,
      body: [
        ['Pre-operative', fmtR(c.preOperativeExpense)],
        ['Land & Building', fmtR(c.totalLand)],
        ['Plant / Machinery', fmtR(c.totalMachinery)],
        ['Furniture', fmtR(c.totalFurniture)],
        ['Working Capital', fmtR(c.workingCapital)],
        [{ content: 'TOTAL PROJECT COST', styles: { fontStyle: 'bold', fillColor: [220, 252, 231] } }, { content: fmtR(c.totalProjectCost), styles: { fontStyle: 'bold', fillColor: [220, 252, 231] } }],
      ],
      styles: { fontSize: 8.5, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 100 } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 6;

    // -------- WORKFLOW & APPROVAL --------
    if (y > 230) { doc.addPage(); y = M; }
    doc.setFontSize(10); doc.setTextColor(4, 120, 87);
    doc.text('WORKFLOW & APPROVAL', M, y); y += 2;
    autoTable(doc, {
      startY: y,
      head: [['Role', 'Officer', 'Status / Date']],
      body: [
        ['Prepared by (Mentor / CRP-EP)', plan.crp_ep_name || plan.created_by_email || entrepreneur.crp_ep_name || '—', plan.created_at ? new Date(plan.created_at).toLocaleDateString('en-IN') : '—'],
        ['Submitted by', plan.submitted_by_email || plan.created_by_email || '—', plan.submitted_at ? new Date(plan.submitted_at).toLocaleDateString('en-IN') : (plan.status === 'draft' ? 'Pending' : '—')],
        ['Reviewed / Checked', plan.checked_by_email || '—', plan.checked_at ? new Date(plan.checked_at).toLocaleDateString('en-IN') : '—'],
        ['Approved by (Admin)', plan.approved_by_email || '—', plan.approved_at ? new Date(plan.approved_at).toLocaleDateString('en-IN') : (plan.status === 'approved' ? 'Approved' : 'Pending')],
        ...(plan.rejection_reason ? [['Rejected by', plan.rejected_by_email || '—', `${plan.rejected_at ? new Date(plan.rejected_at).toLocaleDateString('en-IN') : '—'} — Reason: ${plan.rejection_reason}`]] : []),
      ],
      styles: { fontSize: 8.5, cellPadding: 2 },
      headStyles: { fillColor: [4, 120, 87], textColor: 255 },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 8;

    // -------- SIGNATURES --------
    if (y > 240) { doc.addPage(); y = M; }
    doc.setFontSize(10); doc.setTextColor(4, 120, 87);
    doc.text('SIGNATURES', M, y); y += 8;
    const sigW = (pageW - 2 * M - 8) / 2;
    const sigBlocks = [
      ['Mentor / CRP-EP (Prepared by)', plan.crp_ep_name || plan.created_by_email || ''],
      ['Branch Manager / Reviewer', plan.checked_by_email || ''],
      ['Approver (Admin)', plan.approved_by_email || ''],
      ['Applicant (Entrepreneur)', entrepreneur.name || ''],
    ];
    sigBlocks.forEach((sig, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = M + col * (sigW + 8);
      const yy = y + row * 22;
      doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.4);
      doc.line(x, yy + 10, x + sigW, yy + 10);
      doc.setFontSize(7.5); doc.setTextColor(100);
      doc.text(sig[0], x, yy + 14);
      doc.setFontSize(8.5); doc.setTextColor(15, 23, 42);
      doc.text(sig[1] || ' ', x, yy + 18);
    });
    y += 50;

    // Approved stamp
    if (plan.status === 'approved') {
      doc.setFillColor(220, 252, 231);
      doc.setDrawColor(22, 163, 74);
      doc.rect(M, y, pageW - 2 * M, 9, 'FD');
      doc.setFontSize(10); doc.setTextColor(21, 128, 61);
      doc.text(`✓ APPROVED — Plan Sanctioned${plan.approved_at ? ` on ${new Date(plan.approved_at).toLocaleDateString('en-IN')}` : ''}`, pageW / 2, y + 6, { align: 'center' });
    }

    doc.save(`BusinessPlan_${(entrepreneur.name || 'plan').replace(/\s+/g, '_')}.pdf`);
  };

  if (loading || !plan) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8 w-full" data-testid="business-plan-form">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Link to="/business-plans" className="text-slate-500 hover:text-slate-700 flex-shrink-0"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold text-slate-900 truncate">Business Plan — {entrepreneur?.name}</h1>
            <p className="text-[10px] sm:text-xs text-slate-500 truncate">{entrepreneur?.enrollment_no} · {branch?.name || '—'} · Status: <b className="capitalize">{plan.status.replace('_',' ')}</b>{plan.rejection_reason ? <span className="ml-2 text-rose-600">· Rejected: {plan.rejection_reason}</span> : null}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="bp-print"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700" data-testid="bp-excel"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          <Button variant="outline" onClick={handlePDF} className="border-rose-300 text-rose-700" data-testid="bp-pdf"><FileText className="w-4 h-4 mr-1" />PDF</Button>
          {!isReadOnly && <Button onClick={handleSave} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="bp-save"><Save className="w-4 h-4 mr-1" />Save</Button>}
          {!isReadOnly && (plan.status === 'draft' || plan.status === 'rejected') && <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bp-submit"><Send className="w-4 h-4 mr-1" />Submit for Approval</Button>}
          {plan.status === 'pending_approval' && canApprove && <Button onClick={handleApprove} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bp-approve"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>}
          {plan.status === 'pending_approval' && canApprove && <Button variant="outline" onClick={() => setShowReject(true)} className="border-rose-300 text-rose-700" data-testid="bp-reject"><XCircle className="w-4 h-4 mr-1" />Reject</Button>}
        </div>
      </div>

      {/* Status banners — guide the user about what they can do */}
      {plan.status === 'pending_approval' && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-3 mb-4 flex items-start gap-2">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Submitted — awaiting approval</p>
            <p className="text-amber-800 text-xs">This plan is now read-only. The Mentor / Admin / Checker will review it. If they reject it, the plan will return to you for rectification.</p>
          </div>
        </div>
      )}
      {plan.status === 'rejected' && (
        <div className="bg-rose-50 border-l-4 border-rose-500 rounded-md p-3 mb-4 flex items-start gap-2">
          <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-rose-900">Rejected — please rectify and re-submit</p>
            {plan.rejection_reason && <p className="text-rose-800 text-xs mt-0.5"><b>Reason:</b> {plan.rejection_reason}</p>}
            <p className="text-rose-700 text-xs mt-1">You can now edit the plan. Once corrected, click <b>Submit for Approval</b> again.</p>
          </div>
        </div>
      )}
      {plan.status === 'approved' && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-md p-3 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-900">Approved</p>
            <p className="text-emerald-800 text-xs">This plan is sanctioned. It can now be linked to a loan from the Business Plans list.</p>
          </div>
        </div>
      )}

      {/* Project Summary — visible only to Admin & Branch (Mentor) */}
      {(['admin', 'branch'].includes((user?.role || '').toLowerCase())) && (
        <ProjectSummary plan={plan} calc={calc} />
      )}

      {/* 1.0 — Auto-fetched + small overrides */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-bold text-emerald-800 mb-2">1.0 Entrepreneur &amp; Enterprise (auto-fetched)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div><b>Name:</b> {entrepreneur?.name || '-'}</div>
          <div><b>Mobile:</b> {entrepreneur?.mobile || '-'}</div>
          <div><b>Aadhaar:</b> {entrepreneur?.aadhaar_number || '-'}</div>
          <div><b>Gender:</b> {entrepreneur?.gender || '-'}</div>
          <div><b>Enterprise:</b> {entrepreneur?.enterprise?.name || '-'}</div>
          <div><b>Sector:</b> {entrepreneur?.enterprise?.type || '-'}</div>
          <div><b>Operation:</b> {entrepreneur?.enterprise?.operation_type || '-'}</div>
          <div><b>SHG/VLF:</b> {entrepreneur?.shg_vlf_name || '-'}</div>
          <div><b>Product/Item:</b> {entrepreneur?.enterprise?.product_item || '-'}</div>
          <div><b>Ownership:</b> {entrepreneur?.enterprise?.ownership_structure || '-'}</div>
          <div><b>Govt. License:</b> {entrepreneur?.enterprise?.govt_license || '-'}</div>
          <div><b>Enterprise Type:</b> {entrepreneur?.enterprise?.is_existing ? 'Existing' : 'New'}</div>
          <div><b>State:</b> {entrepreneur?.state || '-'}</div>
          <div><b>District:</b> {entrepreneur?.district || '-'}</div>
          <div><b>Block:</b> {entrepreneur?.block || '-'}</div>
          <div><b>Gram Panchayat:</b> {entrepreneur?.gram_panchayat || '-'}</div>
          <div><b>Village:</b> {entrepreneur?.village || '-'}</div>
          <div><b>Pincode:</b> {entrepreneur?.pincode || '-'}</div>
          <div className="col-span-2"><b>Address of Unit:</b> {entrepreneur?.address || '-'}</div>
          <div className="col-span-2"><b>CRP-EP / Mentor:</b> {plan.crp_ep_name || entrepreneur?.crp_ep_name || '-'}</div>
        </div>
        <p className="text-xs text-emerald-700 mt-2">Missing info? <Link to={`/entrepreneurs/${entrepreneur?.id}`} className="underline">Edit the entrepreneur record</Link>.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><Label className="text-xs">Plan Date</Label><Input type="date" value={plan.plan_date || ''} onChange={e => updateField('plan_date', e.target.value)} disabled={isReadOnly} /></div>
        <div><Label className="text-xs">Repayment (months)</Label><Input type="number" value={plan.repayment_period_months} onChange={e => updateField('repayment_period_months', Number(e.target.value || 0))} disabled={isReadOnly} /></div>
        <div><Label className="text-xs">Years of Experience in business</Label><Input type="number" value={plan.experience_years} onChange={e => updateField('experience_years', Number(e.target.value || 0))} disabled={isReadOnly} /></div>
        <div className="md:col-span-3"><Label className="text-xs">Line of Business / Purpose</Label><Input value={plan.line_of_business || ''} onChange={e => updateField('line_of_business', e.target.value)} disabled={isReadOnly} /></div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <RowsTable title="2.0 Production & Revenue Planning (for 1 year)" subtitle="Pick the duration unit (Day / Week / Month) first; all totals auto-convert to a yearly figure." rows={plan.products} readOnly={isReadOnly}
          onChange={v => updateField('products', v)} addLabel="Product"
          columns={{ template: TEMPLATES.product, minWidth: '1500px', cols: [
            { key: 'name', label: 'Product Name', minWidth: '160px' },
            { key: 'duration_unit', label: 'Unit', type: 'select', options: ['Day', 'Week', 'Month'], default: 'Month', minWidth: '90px' },
            { key: 'working_months', label: 'Duration', type: 'number', minWidth: '90px' },
            { key: '_yearlyEq', label: 'Yearly Equiv.', align: 'text-right', minWidth: '95px', compute: r => {
              const u = r.duration_unit || 'Month';
              const v = num(r.working_months);
              if (u === 'Day') return `${(v / 365 * 12).toFixed(1)} mo`;
              if (u === 'Week') return `${(v / 52 * 12).toFixed(1)} mo`;
              return `${v.toFixed(1)} mo`;
            }},
            { key: 'prod_per_duration', label: 'Qty / Duration', type: 'number', minWidth: '110px' },
            { key: '_totalProd', label: 'Total Prod. (yr)', align: 'text-right', minWidth: '110px', compute: r => num(r.working_months) * (num(r.prod_per_duration) || (num(r.qty_per_year || 0) / Math.max(1, num(r.working_months)))) },
            { key: 'utilization_pct', label: 'Util %', type: 'number', minWidth: '80px' },
            { key: '_perDurUtil', label: 'Per Dura. Utili.', align: 'text-right', minWidth: '110px', compute: r => num(r.prod_per_duration) * num(r.utilization_pct) / 100 },
            { key: '_totalUtil', label: 'Total Util. (yr)', align: 'text-right', minWidth: '120px', compute: r => {
              const perDur = num(r.prod_per_duration);
              const totalProd = perDur > 0 ? num(r.working_months) * perDur : num(r.qty_per_year || 0);
              return totalProd * num(r.utilization_pct) / 100;
            }},
            { key: 'sale_price_per_unit', label: 'Sale Rs./Unit', type: 'number', align: 'text-right', minWidth: '120px' },
            { key: '_rev', label: 'Sales Revenue (yr)', align: 'text-right', minWidth: '140px', compute: r => {
              const perDur = num(r.prod_per_duration);
              const totalProd = perDur > 0 ? num(r.working_months) * perDur : num(r.qty_per_year || 0);
              return totalProd * num(r.utilization_pct) / 100 * num(r.sale_price_per_unit);
            }, format: fmtR },
            { key: 'cost_per_unit', label: 'Cost Rs./Unit', type: 'number', align: 'text-right', minWidth: '120px' },
            { key: '_cost', label: 'Cost of Goods (yr)', align: 'text-right', minWidth: '140px', compute: r => {
              const perDur = num(r.prod_per_duration);
              const totalProd = perDur > 0 ? num(r.working_months) * perDur : num(r.qty_per_year || 0);
              return totalProd * num(r.utilization_pct) / 100 * num(r.cost_per_unit);
            }, format: fmtR },
          ]}}
          computedFooter={<><td colSpan={11} className="px-2 py-1.5">TOTAL</td><td className="px-2 py-1.5 text-right">{fmtR(calc.totalRevenue)}</td><td className="px-2 py-1.5"></td><td className="px-2 py-1.5 text-right">{fmtR(calc.totalProductCost)}</td><td /></>}
        />

        <RowsTable title="2.1 Other Income" rows={plan.other_income} readOnly={isReadOnly}
          onChange={v => updateField('other_income', v)} addLabel="Income"
          columns={{ template: TEMPLATES.other_income, cols: [
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount', type: 'number', align: 'text-right' },
          ]}}
        />

        {/* ============== EXPENDITURE SECTION ============== */}
        <div className="bg-gradient-to-r from-rose-50 via-rose-100 to-rose-50 border-l-4 border-rose-500 rounded-md p-3 mt-2">
          <h2 className="text-base font-bold text-rose-900">Expenditure</h2>
          <p className="text-[11px] text-rose-700">All cost components for the project</p>
        </div>

        {/* Pre-operative Expense (with itemized dropdown) — moved to top of Expenditure */}
        <RowsTable title="Pre-operative Expenses" subtitle="Add each pre-operative item — pick a category or choose Others to specify your own."
          rows={plan.pre_operative_items || []} readOnly={isReadOnly}
          onChange={v => updateField('pre_operative_items', v)} addLabel="Item"
          columns={{ template: TEMPLATES.pre_operative_items, cols: [
            { key: 'type', label: 'Category', type: 'select-other', options: ['Market Survey', 'Stationary', 'Shop Establishment Fees', 'Legal Fees', 'Others'], default: '' },
            { key: 'description', label: 'Description / Notes' },
            { key: 'amount', label: 'Amount (Rs.)', type: 'number', align: 'text-right' },
          ]}}
          computedFooter={<><td colSpan={2} className="px-2 py-1.5">TOTAL</td><td className="px-2 py-1.5 text-right">{fmtR(calc.preOperativeExpense || 0)}</td><td /></>}
        />

        {/* Pre-loan Interest (single-value field — kept compact) */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label className="text-xs">Pre-loan Interest (if any)</Label>
            <Input type="number" value={plan.pre_loan_interest} onChange={e => updateField('pre_loan_interest', Number(e.target.value||0))} disabled={isReadOnly} />
            <p className="text-[10px] text-slate-500 mt-0.5">Interest accrued before the project starts (if any)</p>
          </div>
          <div className="md:col-span-2 flex items-end">
            <p className="text-[11px] text-slate-500">Pre-operative items above are summed automatically into the Total Project Cost. Use the Others option to specify any custom expense type.</p>
          </div>
        </div>

        <RowsTable title="3.1 Land & Building" rows={plan.land_building} readOnly={isReadOnly}
          onChange={v => updateField('land_building', v)} addLabel="Entry"
          columns={{ template: TEMPLATES.land_building, cols: [
            { key: 'ownership', label: 'Ownership', type: 'select-other', options: ['Own Land', 'Rental', 'Other'], default: 'Own Land' },
            { key: 'amount', label: 'Amount', type: 'number', align: 'text-right' },
          ]}}
        />

        <RowsTable title="3.2 Plant / Machinery / Equipment" rows={plan.plant_machinery} readOnly={isReadOnly}
          onChange={v => updateField('plant_machinery', v)} addLabel="Item"
          columns={{ template: TEMPLATES.plant_machinery, minWidth: '1200px', cols: [
            { key: 'particulars', label: 'Particulars', minWidth: '160px' },
            { key: 'nos', label: 'Nos', type: 'number', minWidth: '70px' },
            { key: 'unit_price', label: 'Unit Price', type: 'number', align: 'text-right', minWidth: '110px' },
            { key: '_t', label: 'Total', align: 'text-right', minWidth: '110px', compute: r => num(r.nos) * num(r.unit_price), format: fmtR },
            { key: 'scrap_value', label: 'Scrap Value', type: 'number', align: 'text-right', minWidth: '100px' },
            { key: 'life_years', label: 'Life (yrs)', type: 'number', minWidth: '80px' },
            { key: '_dep', label: 'Depr. / yr', align: 'text-right', minWidth: '110px',
              compute: r => Math.max(0, (num(r.nos) * num(r.unit_price) - num(r.scrap_value)) / (num(r.life_years) || 1)), format: fmtR },
            { key: '_bv', label: 'Book Value', align: 'text-right', minWidth: '110px',
              compute: r => Math.max(0, (num(r.nos) * num(r.unit_price)) - Math.max(0, (num(r.nos) * num(r.unit_price) - num(r.scrap_value)) / (num(r.life_years) || 1))), format: fmtR },
            { key: 'supplier', label: 'Supplier', minWidth: '120px' },
          ]}}
        />

        <RowsTable title="3.3 Furniture" rows={plan.furniture} readOnly={isReadOnly}
          onChange={v => updateField('furniture', v)} addLabel="Item"
          columns={{ template: TEMPLATES.furniture, minWidth: '1200px', cols: [
            { key: 'particulars', label: 'Particulars', minWidth: '160px' },
            { key: 'nos', label: 'Nos', type: 'number', minWidth: '70px' },
            { key: 'unit_price', label: 'Unit Price', type: 'number', align: 'text-right', minWidth: '110px' },
            { key: '_t', label: 'Total', align: 'text-right', minWidth: '110px', compute: r => num(r.nos) * num(r.unit_price), format: fmtR },
            { key: 'scrap_value', label: 'Scrap Value', type: 'number', align: 'text-right', minWidth: '100px' },
            { key: 'life_years', label: 'Life (yrs)', type: 'number', minWidth: '80px' },
            { key: '_dep', label: 'Depr. / yr', align: 'text-right', minWidth: '110px',
              compute: r => Math.max(0, (num(r.nos) * num(r.unit_price) - num(r.scrap_value)) / (num(r.life_years) || 1)), format: fmtR },
            { key: '_bv', label: 'Book Value', align: 'text-right', minWidth: '110px',
              compute: r => Math.max(0, (num(r.nos) * num(r.unit_price)) - Math.max(0, (num(r.nos) * num(r.unit_price) - num(r.scrap_value)) / (num(r.life_years) || 1))), format: fmtR },
            { key: 'supplier', label: 'Supplier', minWidth: '120px' },
          ]}}
        />

        <RowsTable title="3.4 Raw Material" rows={plan.raw_material} readOnly={isReadOnly}
          onChange={v => updateField('raw_material', v)} addLabel="Material"
          columns={{ template: TEMPLATES.raw_material, cols: [
            { key: 'item', label: 'Item' },
            { key: 'frequency_per_year', label: 'Freq/Yr', type: 'number' },
            { key: 'quantity', label: 'Quantity', type: 'number' },
            { key: 'unit_rate', label: 'Unit Rate', type: 'number', align: 'text-right' },
            { key: '_t', label: 'Total', align: 'text-right', compute: r => num(r.frequency_per_year) * num(r.quantity) * num(r.unit_rate), format: fmtR },
          ]}}
          computedFooter={<><td colSpan={4} className="px-2 py-1.5">TOTAL RAW MATERIAL</td><td className="px-2 py-1.5 text-right">{fmtR(calc.totalRaw || 0)}</td><td /></>}
        />

        {/* Reconciliation rule: Section 3.4 Raw Material Total must be >= Section 2.0 Cost of Goods (yr).
            Raw material may be higher (e.g. wastage, buffer stock) but cannot be lower than what is sold. */}
        {(() => {
          const cog = num(calc.totalProductCost);
          const rm = num(calc.totalRaw);
          if (cog === 0 && rm === 0) return null;
          if (rm >= cog) {
            const surplus = rm - cog;
            return (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-md p-3 flex items-start gap-2" data-testid="cog-rm-match">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-emerald-900">Raw Material covers Cost of Goods</p>
                  <p className="text-emerald-700 text-xs">
                    Section 3.4 Raw Material Total = <b>{fmtR(rm)}</b> ≥ Section 2.0 Cost of Goods (yr) = <b>{fmtR(cog)}</b>.
                    {surplus > 0 && <> Surplus / buffer: <b>{fmtR(surplus)}</b>.</>}
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div className="bg-rose-50 border-l-4 border-rose-500 rounded-md p-3 flex items-start gap-2" data-testid="cog-rm-mismatch">
              <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-rose-900">Raw Material shortfall — submission blocked</p>
                <p className="text-rose-800 text-xs">
                  The <b>Raw Material Total</b> in Section 3.4 ({fmtR(rm)}) must be at least equal to the <b>Total Cost of Goods (yr)</b> in Section 2.0 ({fmtR(cog)}).
                  Shortfall: <b>{fmtR(cog - rm)}</b>.
                </p>
                <p className="text-rose-700 text-[11px] mt-1">Add more raw-material entries or increase quantities so the Raw Material Total is greater than or equal to the Cost of Goods before submitting for approval.</p>
              </div>
            </div>
          );
        })()}

        <RowsTable title="3.5 Utilities" rows={plan.utilities} readOnly={isReadOnly} onChange={v => updateField('utilities', v)} addLabel="Utility"
          columns={{ template: TEMPLATES.utilities, cols: [
            { key: 'type', label: 'Type', type: 'select-other', options: ['Electric Bill', 'Water Bill', 'Gas', 'Internet', 'Mobile Recharge', 'Municipal Tax', 'Others'] },
            { key: 'yearly_amount', label: 'Yearly Amount', type: 'number', align: 'text-right' }
          ]}}
        />

        <RowsTable title="3.6 Manpower (Salaries / Wages)" rows={plan.manpower} readOnly={isReadOnly} onChange={v => updateField('manpower', v)} addLabel="Role"
          columns={{ template: TEMPLATES.manpower, cols: [
            { key: 'role', label: 'Role' },
            { key: 'monthly_wage', label: 'Monthly Wage', type: 'number', align: 'text-right' },
            { key: 'months', label: 'Months', type: 'number' },
            { key: 'count', label: 'Count', type: 'number' },
            { key: '_t', label: 'Total', align: 'text-right', compute: r => num(r.monthly_wage) * num(r.months) * num(r.count), format: fmtR },
          ]}}
        />

        <RowsTable title="3.7 Administrative Expenses" rows={plan.admin_expenses} readOnly={isReadOnly} onChange={v => updateField('admin_expenses', v)} addLabel="Item"
          columns={{ template: TEMPLATES.admin_expenses, cols: [{ key: 'type', label: 'Type' }, { key: 'yearly_amount', label: 'Yearly Amount', type: 'number', align: 'text-right' }]}}
        />

        <RowsTable title="3.8 Selling & Distribution" rows={plan.selling_expenses} readOnly={isReadOnly} onChange={v => updateField('selling_expenses', v)} addLabel="Item"
          columns={{ template: TEMPLATES.selling_expenses, cols: [{ key: 'type', label: 'Type' }, { key: 'yearly_amount', label: 'Yearly Amount', type: 'number', align: 'text-right' }]}}
        />

        {/* Section 3.9 Working Capital — matches Excel rows 100-107 */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2 bp-rows-table">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-semibold text-slate-900">3.9 Working Capital</h3>
            {!isReadOnly && (
              <Button size="sm" variant="outline" onClick={() => updateField('working_capital', [...(plan.working_capital || []), { ...TEMPLATES.working_capital }])} className="h-8 text-sm">
                <Plus className="w-4 h-4 mr-1" />Item
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-2.5 py-2 text-left font-semibold text-slate-700">Working Capital Type</th>
                  <th className="px-2.5 py-2 text-right font-semibold text-slate-700">Holding Period (Days)</th>
                  <th className="px-2.5 py-2 text-right font-semibold text-slate-700">Holding Inv. Qty</th>
                  <th className="px-2.5 py-2 text-right font-semibold text-slate-700">Unit Rate</th>
                  <th className="px-2.5 py-2 text-right font-semibold text-slate-700">Inventory Cost</th>
                  <th className="px-2.5 py-2 text-left font-semibold text-slate-700">Description</th>
                  {!isReadOnly && <th />}
                </tr>
              </thead>
              <tbody>
                {(plan.working_capital || []).length === 0 ? (
                  <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400 italic">No entries — add items like Raw stock, Finished goods, Sales on credit</td></tr>
                ) : plan.working_capital.map((r, i) => {
                  const update = (k, v) => updateField('working_capital', plan.working_capital.map((rr, ii) => ii === i ? { ...rr, [k]: v } : rr));
                  const remove = () => updateField('working_capital', plan.working_capital.filter((_, ii) => ii !== i));
                  const invCost = num(r.holding_qty) * num(r.unit_rate);
                  return (
                    <tr key={`wc-${i}-${r.type || ''}`} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-1.5 py-1.5"><Input value={r.type ?? ''} onChange={e => update('type', e.target.value)} className="h-9 text-sm text-slate-900 font-medium" disabled={isReadOnly} /></td>
                      <td className="px-1.5 py-1.5"><Input type="number" value={r.holding_days ?? 0} onChange={e => update('holding_days', Number(e.target.value || 0))} className="h-9 text-sm text-right text-slate-900 font-medium" disabled={isReadOnly} /></td>
                      <td className="px-1.5 py-1.5"><Input type="number" value={r.holding_qty ?? 0} onChange={e => update('holding_qty', Number(e.target.value || 0))} className="h-9 text-sm text-right text-slate-900 font-medium" disabled={isReadOnly} /></td>
                      <td className="px-1.5 py-1.5"><Input type="number" value={r.unit_rate ?? 0} onChange={e => update('unit_rate', Number(e.target.value || 0))} className="h-9 text-sm text-right text-slate-900 font-medium" disabled={isReadOnly} /></td>
                      <td className="px-2.5 py-1.5 text-right font-semibold text-slate-900">{fmtR(invCost)}</td>
                      <td className="px-1.5 py-1.5"><Input value={r.description ?? ''} onChange={e => update('description', e.target.value)} className="h-9 text-sm text-slate-900 font-medium" disabled={isReadOnly} /></td>
                      {!isReadOnly && <td className="px-1.5 py-1.5"><button onClick={remove} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-4 h-4" /></button></td>}
                    </tr>
                  );
                })}
                {/* Auto-row: Others Exp of 1 month */}
                <tr className="bg-amber-50 italic">
                  <td className="px-2.5 py-2 text-slate-800 text-sm">Others Exp of 1 month <span className="text-[11px] text-slate-500">(Utilities + Manpower + Admin + Selling) / 12</span></td>
                  <td colSpan={3} className="px-2.5 py-2 text-right text-slate-500 text-sm">auto-computed</td>
                  <td className="px-2.5 py-2 text-right font-semibold text-amber-900 text-sm">{fmtR(calc.wcMonthlyOverhead || 0)}</td>
                  <td className="px-2.5 py-2 text-slate-500 text-sm">— monthly overhead carry-cost</td>
                  {!isReadOnly && <td />}
                </tr>
              </tbody>
              <tfoot className="bg-emerald-50 font-bold text-emerald-900">
                <tr>
                  <td colSpan={4} className="px-2.5 py-2 text-sm">Total Working Capital Needed</td>
                  <td className="px-2.5 py-2 text-right text-sm">{fmtR(calc.workingCapital || 0)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Total Cost of the Project */}
        <div className="bg-gradient-to-r from-emerald-50 via-emerald-100 to-emerald-50 border-2 border-emerald-400 rounded-lg p-4 mt-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Total Cost of the Project</p>
              <p className="text-[11px] text-emerald-700 mt-0.5">Pre-op + Land + Plant/Machinery + Furniture + Working Capital</p>
            </div>
            <p className="text-2xl font-bold text-emerald-900">{fmtR(calc.totalProjectCost)}</p>
          </div>
        </div>

      </div>

      {/* ============== 4.0 MEANS OF FINANCE & PROFITABILITY ============== */}
      <div className="bg-gradient-to-r from-indigo-50 via-indigo-100 to-indigo-50 border-l-4 border-indigo-500 rounded-md p-3 mt-6 mb-3">
        <h2 className="text-base font-bold text-indigo-900">4.0 Means of Finance &amp; Profitability</h2>
        <p className="text-[11px] text-indigo-700">Source of funds and key viability metrics</p>
      </div>

      {/* 4.1 Means of Finance */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-3">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">4.1 Means of Finance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Own Investment</Label><Input type="number" value={plan.own_investment} onChange={e => updateField('own_investment', Number(e.target.value||0))} disabled={isReadOnly} /></div>
          <div><Label className="text-xs">Bank Loan</Label><Input type="number" value={plan.bank_loan} onChange={e => updateField('bank_loan', Number(e.target.value||0))} disabled={isReadOnly} /></div>
          <div><Label className="text-xs">CEF Loan</Label><Input type="number" value={plan.cef_loan} onChange={e => updateField('cef_loan', Number(e.target.value||0))} disabled={isReadOnly} /></div>
          <div><Label className="text-xs">CIF / Others</Label><Input type="number" value={plan.cif_others} onChange={e => updateField('cif_others', Number(e.target.value||0))} disabled={isReadOnly} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><p className="text-xs text-emerald-700">Total Fund Arranged</p><p className="text-lg font-bold text-emerald-900">{fmtR(calc.moFinance)}</p></div>
          <div className={`border rounded-lg p-3 ${Math.abs(calc.moFinanceGap) < 1 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}><p className="text-xs text-slate-600">Gap (Project Cost − Fund)</p><p className="text-lg font-bold">{fmtR(calc.moFinanceGap)}</p></div>
        </div>
      </div>

      {/* 4.2 Profitability KPIs */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">4.2 Profitability</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total Income (A)" value={calc.totalIncome} color="emerald" />
          <Kpi label="Cost of Production (B)" value={calc.totalCostOfProduction} color="rose" />
          <Kpi label="Depreciation (C)" value={calc.depreciation} color="amber" />
          <Kpi label="Net Profit (A−B−C)" value={calc.netProfit} color={calc.netProfit >= 0 ? 'emerald' : 'rose'} bold />
          <Kpi label="Total Project Cost" value={calc.totalProjectCost} color="indigo" />
          <Kpi label="Working Capital" value={calc.workingCapital} color="blue" />
          <Kpi label="Break-even Point" value={calc.bep} color="purple" />
          <Kpi label="BEP (months)" rawText={`${calc.bepMonths.toFixed(1)} mo`} color="purple" />
        </div>
      </div>

      {/* Bottom action bar (mirror of top buttons for convenience) */}
      <div className="mt-6 pt-4 border-t-2 border-slate-200">
        <div className="bg-slate-50 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-600">
            <p className="font-semibold text-slate-800">End of Business Plan</p>
            <p>Status: <b className="capitalize">{plan.status.replace('_', ' ')}</b>{plan.rejection_reason ? <span className="ml-2 text-rose-600">· {plan.rejection_reason}</span> : null}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="bp-print-bottom"><Printer className="w-4 h-4 mr-1" />Print</Button>
            <Button variant="outline" onClick={handlePDF} className="border-rose-300 text-rose-700" data-testid="bp-pdf-bottom"><FileText className="w-4 h-4 mr-1" />PDF</Button>
            <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700" data-testid="bp-excel-bottom"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
            {!isReadOnly && <Button onClick={handleSave} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="bp-save-bottom"><Save className="w-4 h-4 mr-1" />Save</Button>}
            {!isReadOnly && (plan.status === 'draft' || plan.status === 'rejected') && <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="bp-submit-bottom"><Send className="w-4 h-4 mr-1" />Submit for Approval</Button>}
            {plan.status === 'pending_approval' && canApprove && <Button onClick={handleApprove} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bp-approve-bottom"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>}
            {plan.status === 'pending_approval' && canApprove && <Button variant="outline" onClick={() => setShowReject(true)} className="border-rose-300 text-rose-700" data-testid="bp-reject-bottom"><XCircle className="w-4 h-4 mr-1" />Reject</Button>}
          </div>
        </div>
      </div>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Business Plan</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason for rejection (sent to creator)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4} data-testid="bp-reject-reason" />
          <Button onClick={handleReject} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="bp-reject-confirm">Confirm Reject</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Kpi = ({ label, value, rawText, color = 'slate', bold }) => {
  const map = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
  };
  return (
    <div className={`border rounded-lg p-3 ${map[color]}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-80">{label}</p>
      <p className={`mt-0.5 ${bold ? 'text-xl' : 'text-base'} font-bold`}>{rawText || fmtR(value)}</p>
    </div>
  );
};

// ---------------- Project Summary (Executive overview + charts) ----------------
const ProjectSummary = ({ plan, calc }) => {
  const c = calc || {};
  const monthlyProfit = (c.netProfit || 0) / 12;
  const proposedLoan = num(c.proposedLoan);
  // Loan recovery / payback (months) = Loan ÷ Monthly Net Profit
  const paybackMonths = monthlyProfit > 0 ? proposedLoan / monthlyProfit : 0;
  const profitMarginPct = (c.totalIncome || 0) > 0 ? ((c.netProfit || 0) / c.totalIncome) * 100 : 0;

  // Bar chart data — Income vs Costs vs Profit
  const barData = [
    { name: 'Income', value: Math.round(c.totalIncome || 0) },
    { name: 'Variable Cost', value: Math.round(c.variableCost || 0) },
    { name: 'Fixed Cost', value: Math.round(c.fixedCost || 0) },
    { name: 'Net Profit', value: Math.round(c.netProfit || 0) },
  ];

  // Pie chart — Cost composition
  const pieData = [
    { name: 'Raw Material', value: Math.round(c.totalRaw || 0) },
    { name: 'Manpower', value: Math.round(c.totalManpower || 0) },
    { name: 'Utilities', value: Math.round(c.totalUtilities || 0) },
    { name: 'Admin', value: Math.round(c.totalAdmin || 0) },
    { name: 'Selling/Distrib.', value: Math.round(c.totalSelling || 0) },
    { name: 'Depreciation', value: Math.round(c.depreciation || 0) },
  ].filter(d => d.value > 0);
  const pieColors = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#64748b'];

  // Cumulative cash flow over 12 months — investment outflow upfront, then monthly profit
  const cashFlow = [];
  let running = -proposedLoan;
  for (let m = 0; m <= 12; m++) {
    cashFlow.push({ month: `M${m}`, value: Math.round(m === 0 ? running : (running += monthlyProfit)) });
  }

  // Health classification
  const isHealthy = (c.netProfit || 0) > 0 && paybackMonths > 0 && paybackMonths <= num(plan.repayment_period_months || 24);
  const healthLabel = !c.totalIncome ? 'Insufficient data' : isHealthy ? 'Viable' : 'Needs review';
  const healthColor = !c.totalIncome ? 'slate' : isHealthy ? 'emerald' : 'amber';

  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 sm:p-5 mb-5 shadow-md">
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold flex items-center gap-2 text-emerald-800">📊 Project Summary</h2>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 hidden sm:block">Executive overview of viability, profitability and recovery</p>
        </div>
        <span className={`px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-full border whitespace-nowrap ${healthColor === 'emerald' ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : healthColor === 'amber' ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-slate-100 border-slate-300 text-slate-700'}`}>
          {healthLabel}
        </span>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-5">
        <SumKpi label="Total Income (yearly)" value={fmtR(c.totalIncome)} accent="emerald" />
        <SumKpi label="Total Cost of Production" value={fmtR(c.totalCostOfProduction)} accent="rose" />
        <SumKpi label="Net Profit (yearly)" value={fmtR(c.netProfit)} accent={c.netProfit >= 0 ? 'emerald' : 'rose'} bold />
        <SumKpi label="Profit Margin" value={`${profitMarginPct.toFixed(1)}%`} accent={profitMarginPct >= 15 ? 'emerald' : profitMarginPct >= 5 ? 'amber' : 'rose'} />
        <SumKpi label="Project Cost" value={fmtR(c.totalProjectCost)} accent="indigo" />
        <SumKpi label="Proposed Loan" value={fmtR(c.proposedLoan)} accent="blue" />
        <SumKpi label="Net Profit / Month" value={fmtR(monthlyProfit)} accent="emerald" />
        <SumKpi
          label="BEP (Break-even ₹)"
          value={c.bepUnreachable ? '— Not reachable' : fmtR(c.bep)}
          accent="purple"
          bold
        />
        <SumKpi
          label="BEP Period"
          value={c.bepUnreachable ? '— Not reachable' : `${(c.bepMonths || 0).toFixed(1)} mo`}
          accent="purple"
        />
        <SumKpi
          label="Loan Recovery Period"
          value={paybackMonths > 0 ? `${paybackMonths.toFixed(1)} mo` : '—'}
          accent={paybackMonths > 0 && paybackMonths <= num(plan.repayment_period_months || 24) ? 'emerald' : 'amber'}
          bold
        />
        <SumKpi label="Repayment Tenure" value={`${plan.repayment_period_months || 0} mo`} accent="slate" />
        <SumKpi label="Sales Revenue (BEP basis)" value={fmtR(c.bepSalesRevenue)} accent="slate" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-2">
        <ChartCard title="Income vs Cost vs Profit">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.5} />
              <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 11 }} />
              <YAxis tick={{ fill: '#475569', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip
                cursor={{ fill: 'rgba(15,23,42,0.05)' }}
                contentStyle={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#0f172a', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelStyle={{ color: '#0f172a', fontWeight: 700 }}
                itemStyle={{ color: '#0f172a' }}
                formatter={v => fmtR(v)}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} minPointSize={4} label={{ position: 'top', fontSize: 10, fill: '#0f172a', fontWeight: 600, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}` }}>
                {barData.map((d, i) => (
                  <Cell key={i} fill={['#10b981', '#f97316', '#8b5cf6', d.value >= 0 ? '#0ea5e9' : '#ef4444'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost Composition">
          {pieData.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-xs text-slate-500">No costs entered yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#0f172a', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}
                  labelStyle={{ color: '#0f172a', fontWeight: 700 }}
                  itemStyle={{ color: '#0f172a' }}
                  formatter={v => fmtR(v)}
                />
                <Legend wrapperStyle={{ fontSize: 10, color: '#475569' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Cumulative Cash Flow (12 mo)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={cashFlow} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" opacity={0.5} />
              <XAxis dataKey="month" tick={{ fill: '#475569', fontSize: 10 }} />
              <YAxis tick={{ fill: '#475569', fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip
                cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                contentStyle={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#0f172a', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelStyle={{ color: '#0f172a', fontWeight: 700 }}
                itemStyle={{ color: '#0f172a' }}
                formatter={v => fmtR(v)}
              />
              <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3, fill: '#0ea5e9' }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-slate-500 mt-1 text-center">Crosses zero at <b className="text-slate-800">{paybackMonths > 0 && paybackMonths <= 12 ? `month ${paybackMonths.toFixed(1)}` : (paybackMonths > 12 ? `month ${paybackMonths.toFixed(1)} (beyond 12)` : '—')}</b> · Loan recovery</p>
        </ChartCard>
      </div>
    </div>
  );
};

const SumKpi = ({ label, value, accent = 'slate', bold }) => {
  const accentBg = {
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    rose: 'bg-rose-50 border-rose-300 text-rose-900',
    indigo: 'bg-indigo-50 border-indigo-300 text-indigo-900',
    blue: 'bg-blue-50 border-blue-300 text-blue-900',
    purple: 'bg-purple-50 border-purple-300 text-purple-900',
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    slate: 'bg-slate-50 border-slate-300 text-slate-800',
  }[accent];
  return (
    <div className={`rounded-lg border ${accentBg} px-2 sm:px-3 py-1.5 sm:py-2`}>
      <p className="text-[8px] sm:text-[9px] uppercase tracking-wider opacity-70 font-semibold leading-tight">{label}</p>
      <p className={`${bold ? 'text-sm sm:text-lg' : 'text-xs sm:text-sm'} font-bold mt-0.5 break-words`}>{value}</p>
    </div>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
    <p className="text-xs font-semibold text-emerald-800 mb-2">{title}</p>
    {children}
  </div>
);

export default BusinessPlanForm;
